import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { useState, useContext } from "react";
import { Upload, Button, Card, Tabs, Select } from "antd";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";

const { TabPane } = Tabs;
const { Option } = Select;

export default function BulkUpload() {
    const { authData, backUrl } = useContext(AuthContext);

    // Employee Upload States
    const [empFile, setEmpFile] = useState(null);
    const [empUploading, setEmpUploading] = useState(false);

    // Salary Upload States
    const [salaryFile, setSalaryFile] = useState(null);
    const [salaryUploading, setSalaryUploading] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);

    // Leave Upload States
    const [leaveFile, setLeaveFile] = useState(null);
    const [leaveUploading, setLeaveUploading] = useState(false);

    // Generic upload props
    const createUploadProps = (setFileCallback) => ({
        beforeUpload: (file) => {
            const allowed = [
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/vnd.ms-excel"
            ];
            if (!allowed.includes(file.type)) {
                toast.error("Please upload a valid Excel file (.xlsx, .xls)");
                return Upload.LIST_IGNORE;
            }
            setFileCallback(file);
            return false;
        },
        maxCount: 1,
        onRemove: () => setFileCallback(null),
    });

    // Employee Bulk Upload
    const bulkUploadEmployees = async () => {
        if (!empFile) {
            toast.error("Please select an Excel file first!");
            return;
        }

        const formData = new FormData();
        formData.append("file", empFile);

        try {
            setEmpUploading(true);
            const res = await axios.post(
                `${backUrl}/api/payroll/bulk_upload_employees/`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );

            if (res.data.errors && res.data.errors.length > 0) {
                toast.error(
                    `Some items failed to upload: ${res.data.errors.join(", ")}`
                );
            } else {
                toast.success(
                    `${res.data.message} (${res.data.success_count} items uploaded successfully)`
                );
            }

            toast.success("Employees uploaded successfully!");
            setEmpFile(null);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Upload failed! Please check the file format.");
        } finally {
            setEmpUploading(false);
        }
    };

    // Salary Bulk Upload
    const bulkUploadSalary = async () => {
        if (!salaryFile) {
            toast.error("Please select an Excel file first!");
            return;
        }
        if (!selectedMonth || !selectedYear) {
            toast.error("Please select both month and year!");
            return;
        }

        const formData = new FormData();
        formData.append("file", salaryFile);
        formData.append("month", selectedMonth);
        formData.append("year", selectedYear);

        try {
            setSalaryUploading(true);
            await axios.post(
                `${backUrl}/api/payroll/upload-base-salary/`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );
            toast.success("Salary data uploaded successfully!");
            setSalaryFile(null);
            setSelectedMonth(null);
            setSelectedYear(null);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Upload failed! Please check the file format.");
        } finally {
            setSalaryUploading(false);
        }
    };

    // Leave Bulk Upload
    const bulkUploadLeave = async () => {
        if (!leaveFile) {
            toast.error("Please select an Excel file first!");
            return;
        }

        const formData = new FormData();
        formData.append("file", leaveFile);

        try {
            setLeaveUploading(true);
            await axios.post(
                `${backUrl}/api/hr/leave-balance/upload/`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );
            toast.success("Leave data uploaded successfully!");
            setLeaveFile(null);
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "Upload failed! Please check the file format.");
        } finally {
            setLeaveUploading(false);
        }
    };

    // Generate month and year options
    const months = [
        { value: "1", label: "January" },
        { value: "2", label: "February" },
        { value: "3", label: "March" },
        { value: "4", label: "April" },
        { value: "5", label: "May" },
        { value: "6", label: "June" },
        { value: "7", label: "July" },
        { value: "8", label: "August" },
        { value: "9", label: "September" },
        { value: "10", label: "October" },
        { value: "11", label: "November" },
        { value: "12", label: "December" },
    ];

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

    // Download Sample Template for Employee Bulk Upload
    const handleDownloadEmployeeTemplate = () => {
        try {
            const headers = [
                'emp_code', 'first_name', 'last_name', 'official_email_id', 'email_id',
                'is_superuser', 'organisation', 'department', 'designation', 'reporting_to',
                'career_history', 'education_history', 'reference_history', 'child_details',
                'sibling_details', 'salutation', 'initial', 'surname',
                'official_cug_number', 'date_of_birth', 'date_of_joining', 'wedding_date',
                'spouse_dob', 'last_working_day', 'date_of_leaving', 'probation_end_date',
                'place_of_birth', 'location', 'place_of_work', 'gender',
                'mother_tongue', 'religion', 'blood_group', 'height', 'weight',
                'marital_status', 'father_name', 'father_profession', 'mother_name',
                'mother_profession', 'spouse_name', 'aadhaar_card_number', 'pan_card_number',
                'esic_ip_number', 'uan_number', 'bank_account_number', 'bank_name',
                'branch_name', 'ifsc_code', 'emergency_contact_person',
                'emergency_contact_number', 'address_for_communication', 'area', 'city',
                'state', 'country', 'pincode', 'permanent_address_for_communication',
                'permanent_area', 'permanent_city', 'permanent_state', 'permanent_country',
                'permanent_pincode', 'is_hr', 'is_hod', 'is_shift', 'is_ot', 'is_active',
                'is_management', 'is_special', 'present_address',
                'landline_extension', 'cub_account_number', 'status'
            ];

            const sampleRow = [
                'EMP001', 'John', 'Doe', 'john.official@company.com', 'john.personal@gmail.com',
                'no', 'Head Office', 'IT', 'Software Engineer', 'EMP000',
                '', '', '', '',
                '', 'Mr', 'A', 'Doe',
                '9876543210', '1995-05-15', '2023-01-10', '',
                '', '', '', '',
                'Chennai', 'Head Office', 'Head Office', 'MALE', 'GENERAL',
                'Tamil', 'Hindu', 'O+', '175', '70',
                'SINGLE', 'Robert Doe', 'Business', 'Mary Doe',
                'Homemaker', '', '123456789012', 'ABCDE1234F',
                '3100123456', '100012345678', '50100012345678', 'HDFC Bank',
                'Chennai Main', 'HDFC0000001', 'Robert Doe',
                '9876543211', '123 Main Street', 'Central', 'Chennai',
                'Tamil Nadu', 'India', '600001', '123 Main Street',
                'Central', 'Chennai', 'Tamil Nadu', 'India',
                '600001', 'no', 'no', 'no', 'no', 'yes',
                'no', 'no', 'yes',
                '', '', 'Probation'
            ];

            const ws = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
            ws["!cols"] = headers.map(h => ({ wch: Math.max(h.length + 3, 15) }));

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Employee_Template");

            XLSX.writeFile(wb, "employee_bulk_upload_template.xlsx");
            toast.success("Sample template downloaded successfully!");
        } catch (error) {
            console.error("Error downloading template:", error);
            toast.error("Failed to download sample template.");
        }
    };

    const SALARY_TEMPLATE_HEADERS = [
        "emp_code",
        "emp_name",
        "designation",
        "department",
        "doj",
        "bank_name",
        "ifsc_code",
        "account_no",
        "bank_branch",
        "pf_uan",
        "esci_no",
        "pan_no",
        "aadhar_no",
        "basic_pm",
        "hra_pm",
        "conveyance_pm",
        "other_allowance_pm",
        "gross_pm",
        "pf_employer_pm",
        "esi_employer_pm",
        "ctc_pm",
        "pf_employee_pm",
        "esi_employee_pm",
        "deductions_pm",
        "net_pay_pm",
        "pt_pm",
        "basic_py",
        "hra_py",
        "conveyance_py",
        "other_allowance_py",
        "gross_py",
        "pf_employer_py",
        "esi_employer_py",
        "ctc_py",
        "pf_employee_py",
        "esi_employee_py",
        "deductions_py",
        "net_pay_py",
        "pt_py",
        "is_pf",
        "is_esi",
        "is_ot",
        "salary_type",
        "remarks",
    ];

    const SALARY_SAMPLE_ROW = [
        "EMP001",
        "John Doe",
        "Software Engineer",
        "IT",
        "2023-01-10",
        "HDFC Bank",
        "HDFC0000001",
        "50100012345678",
        "Chennai Main",
        "100012345678",
        "3100123456",
        "ABCDE1234F",
        "123456789012",
        "25000",
        "12500",
        "2500",
        "5000",
        "45000",
        "1800",
        "0",
        "46800",
        "1800",
        "0",
        "2000",
        "43000",
        "200",
        "300000",
        "150000",
        "30000",
        "60000",
        "540000",
        "21600",
        "0",
        "561600",
        "21600",
        "0",
        "24000",
        "516000",
        "2400",
        "yes",
        "no",
        "yes",
        "CTC",
        "Base salary update",
    ];

    const handleDownloadSalaryTemplate = () => {
        try {
            const ws = XLSX.utils.aoa_to_sheet([SALARY_TEMPLATE_HEADERS, SALARY_SAMPLE_ROW]);
            ws["!cols"] = SALARY_TEMPLATE_HEADERS.map((h) => ({ wch: Math.max(14, h.length + 3) }));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Salary_Upload_Template");
            XLSX.writeFile(wb, "salary_bulk_upload_template.xlsx");
            toast.success("Salary template downloaded successfully!");
        } catch (error) {
            console.error("Error downloading salary template:", error);
            toast.error("Failed to download salary template.");
        }
    };

    return (
        <Card style={{ maxWidth: 600, margin: "20px auto" }}>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
                theme="colored"
            />
            <Tabs defaultActiveKey="1">
                <TabPane tab="Bulk Employee Upload" key="1">
                    <div style={{ padding: "20px 0" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleDownloadEmployeeTemplate}
                                style={{ backgroundColor: "#28a745", color: "white", borderColor: "#28a745" }}
                            >
                                Download Sample Template
                            </Button>
                        </div>

                        <Upload {...createUploadProps(setEmpFile)} fileList={empFile ? [empFile] : []}>
                            <Button icon={<UploadOutlined />}>Click to Select Excel File</Button>
                        </Upload>

                        <Button
                            type="primary"
                            style={{
                                marginTop: 20,
                                width: "100%",
                                backgroundColor: "#0e497b",
                                color: "white"
                            }}
                            loading={empUploading}
                            onClick={bulkUploadEmployees}
                        >
                            Upload Employees
                        </Button>
                    </div>
                </TabPane>

                <TabPane tab="Bulk Salary Upload" key="2">
                    <div style={{ padding: "20px 0" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleDownloadSalaryTemplate}
                                style={{ backgroundColor: "#28a745", color: "white", borderColor: "#28a745" }}
                            >
                                Download Sample Template
                            </Button>
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: "block", marginBottom: 8 }}>Select Month:</label>
                            <Select
                                placeholder="Select Month"
                                style={{ width: "100%" }}
                                value={selectedMonth}
                                onChange={(value) => setSelectedMonth(value)}
                            >
                                {months.map((month) => (
                                    <Option key={month.value} value={month.value}>
                                        {month.label}
                                    </Option>
                                ))}
                            </Select>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <label style={{ display: "block", marginBottom: 8 }}>Select Year:</label>
                            <Select
                                placeholder="Select Year"
                                style={{ width: "100%" }}
                                value={selectedYear}
                                onChange={(value) => setSelectedYear(value)}
                            >
                                {years.map((year) => (
                                    <Option key={year} value={year.toString()}>
                                        {year}
                                    </Option>
                                ))}
                            </Select>
                        </div>

                        <Upload {...createUploadProps(setSalaryFile)} fileList={salaryFile ? [salaryFile] : []}>
                            <Button icon={<UploadOutlined />}>Click to Select Excel File</Button>
                        </Upload>

                        <Button
                            type="primary"
                            style={{
                                marginTop: 20,
                                width: "100%",
                                backgroundColor: "#0e497b",
                                color: "white"
                            }}
                            loading={salaryUploading}
                            onClick={bulkUploadSalary}
                        >
                            Upload Salary Data
                        </Button>
                    </div>
                </TabPane>

                <TabPane tab="Bulk Leave Upload" key="3">
                    <div style={{ padding: "20px 0" }}>
                        <Upload {...createUploadProps(setLeaveFile)} fileList={leaveFile ? [leaveFile] : []}>
                            <Button icon={<UploadOutlined />}>Click to Select Excel File</Button>
                        </Upload>

                        <Button
                            type="primary"
                            style={{
                                marginTop: 20,
                                width: "100%",
                                backgroundColor: "#0e497b",
                                color: "white"
                            }}
                            loading={leaveUploading}
                            onClick={bulkUploadLeave}
                        >
                            Upload Leave Data
                        </Button>
                    </div>
                </TabPane>
            </Tabs>
        </Card>
    );
}